import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AppLayout from '../layout/AppLayout';
import InvoiceDetailDialog from '../billing/InvoiceDetailDialog';
import { daysOverdue, overdueLabel, OVERDUE_COLOR } from '../../utils/overdue';
import { billingService, invoiceService } from '../../services/api';
import { INK, MUTED, RULE, FigureStrip, formatCount, formatINR } from '../analytics/analyticsUi';

const STATUS = {
  draft: { label: 'Draft', color: '#8A94A6' },
  pending: { label: 'Awaiting payment', color: '#C77700' },
  paid: { label: 'Paid', color: '#1BAA52' },
  failed: { label: 'Payment failed', color: '#C2410C' },
  cancelled: { label: 'Cancelled', color: MUTED }
};
const PAYMENT_METHODS = {
  manual: 'Bank transfer / manual',
  upi: 'UPI',
  net_banking: 'Net banking',
  credit_card: 'Credit card',
  debit_card: 'Debit card',
  wallet: 'Wallet',
  offline: 'Cash / cheque'
};

const buttonSx = { textTransform: 'none' };
const KIND_LABELS = { monthly: 'Message charges', subscription: 'Subscription' };

// Bulk status changes and the statuses each one applies to (mirrors the server rules)
const BULK_ACTIONS = [
  { status: 'paid', label: 'Mark as paid', from: ['pending', 'failed', 'draft'] },
  { status: 'failed', label: 'Mark as payment failed', from: ['pending'] },
  { status: 'pending', label: 'Set to awaiting payment', from: ['draft', 'failed', 'cancelled'] },
  { status: 'cancelled', label: 'Cancel', from: ['pending', 'failed', 'draft'], danger: true }
];
const BULK_COPY = {
  paid: { title: 'Mark as paid', button: 'Mark as paid', done: 'marked as paid' },
  failed: { title: 'Mark as payment failed', button: 'Mark as failed', done: 'marked as payment failed', body: 'Customers see these invoices as payment failed.' },
  pending: { title: 'Set to awaiting payment', button: 'Set to awaiting payment', done: 'set to awaiting payment', body: 'Customers see these invoices as awaiting payment. Drafts are sent to the customer and they are notified.' },
  cancelled: { title: 'Cancel invoices', button: 'Cancel invoices', done: 'cancelled', body: "The invoices stay on record as cancelled and aren't counted as billed or exported." }
};
const totalOf = (inv) => inv.total ?? (inv.amount + (inv.taxAmount || 0) - (inv.discountAmount || 0));
const formatDay = (value) => (value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '–');
const todayInput = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Last 13 calendar months, newest first; the default is last month
const monthOptions = () => {
  const now = new Date();
  return Array.from({ length: 13 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return {
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) + (i === 0 ? ' (so far)' : '')
    };
  });
};

const StatusDot = ({ status, overdue = 0 }) => {
  const meta = STATUS[status] || { label: status, color: MUTED };
  return (
    <Box>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: overdue ? OVERDUE_COLOR : meta.color }} />
        <Typography variant="body2" sx={{ color: INK }}>{meta.label}</Typography>
      </Box>
      {overdue > 0 && (
        <Typography variant="caption" sx={{ color: OVERDUE_COLOR, fontWeight: 600, display: 'block', pl: 2 }}>{overdueLabel(overdue)}</Typography>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Generate bills: preview, then create
// ---------------------------------------------------------------------------
const GenerateDialog = ({ open, onClose, onCreated, month: initialMonth }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const months = useMemo(() => monthOptions(), []);
  const [type, setType] = useState('monthly'); // monthly (message charges) | subscription
  const [month, setMonth] = useState(initialMonth || months[1].key);
  const [phase, setPhase] = useState('choose'); // choose | previewing | preview | creating | done
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (open) {
      setPhase('choose');
      setResult(null);
      setError(null);
      setType('monthly');
      setMonth(initialMonth || months[1].key);
    }
    return () => clearTimeout(timer.current);
  }, [open, initialMonth, months]);

  const run = async (dryRun) => {
    setError(null);
    setPhase(dryRun ? 'previewing' : 'creating');
    try {
      const started = await invoiceService.startGenerate(month, { dryRun, type });
      const poll = async () => {
        try {
          const job = (await invoiceService.getGenerateJob(started.data.jobId)).data;
          if (job.status === 'running') {
            timer.current = setTimeout(poll, 2000);
            return;
          }
          if (job.status === 'failed') {
            setError(job.error || 'The billing run failed. Try again.');
            setPhase(dryRun ? 'choose' : 'preview');
            return;
          }
          setResult(job.result);
          setPhase(dryRun ? 'preview' : 'done');
          if (!dryRun) onCreated(job.result);
        } catch (err) {
          setError(err.response?.data?.error || 'Lost contact with the billing run. Check the invoice list before trying again.');
          setPhase(dryRun ? 'choose' : 'preview');
        }
      };
      poll();
    } catch (err) {
      setError(err.response?.data?.error || 'The billing run could not start. Try again.');
      setPhase('choose');
    }
  };

  const busy = phase === 'previewing' || phase === 'creating';
  const billable = result ? result.rows.filter((r) => r.action !== 'skipped') : [];
  const skipped = result ? result.rows.filter((r) => r.action === 'skipped') : [];
  const monthLabel = months.find((m) => m.key === month)?.label || month;

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="lg" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ color: INK, fontWeight: 600 }}>
        {phase === 'done'
          ? `${type === 'subscription' ? 'Subscription invoices' : 'Invoices'} for ${result?.month.label} created`
          : 'Generate monthly bills'}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {(phase === 'choose' || phase === 'previewing') && (
          <Box>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={type}
              disabled={busy}
              onChange={(e, v) => {
                if (!v) return;
                setType(v);
                // Message charges are billed after the month, subscription fees for the month in progress
                setMonth(v === 'subscription' ? months[0].key : months[1].key);
              }}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="monthly" sx={{ ...buttonSx, px: 2 }}>Message charges</ToggleButton>
              <ToggleButton value="subscription" sx={{ ...buttonSx, px: 2 }}>Subscription fees</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" sx={{ color: MUTED, mb: 2, maxWidth: '75ch' }}>
              {type === 'subscription'
                ? 'One invoice per subscription for the plan fee due in the month, pro-rated for partial months, with GST. Monthly plans are charged every month; quarterly and annual plans in the month each cycle starts. Pending subscriptions are not billed.'
                : 'One invoice per WhatsApp account for the calendar month (Indian time): paid messages by category at your message rates, free messages listed at ₹0, and GST. Plan fees are billed separately as subscription invoices, and accounts that Meta bills directly are skipped.'}
              {' '}Re-running a month updates unpaid invoices instead of creating duplicates.
            </Typography>
            <FormControl size="small" sx={{ minWidth: 260, display: 'flex', maxWidth: 260 }} disabled={busy}>
              <InputLabel id="bill-month">Month</InputLabel>
              <Select labelId="bill-month" label="Month" value={month} onChange={(e) => setMonth(e.target.value)}>
                {months.map((m) => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
              </Select>
            </FormControl>
            {phase === 'previewing' && (
              <Box sx={{ mt: 3 }}>
                <LinearProgress />
                <Typography variant="body2" sx={{ color: MUTED, mt: 1 }}>
                  {type === 'subscription' ? 'Working out the plan fees.' : 'Reading message usage from Meta for every account. This takes about a minute.'}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {result && phase !== 'choose' && phase !== 'previewing' && (
          <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2, fontVariantNumeric: 'tabular-nums' }}>
              {[
                [phase === 'done' ? 'Created' : 'New invoices', result.created],
                [phase === 'done' ? 'Updated' : 'Invoices to update', result.updated],
                ['Drafts to check', result.drafts],
                ['Skipped', result.skipped],
                ['Total including GST', formatINR(result.total)]
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>{label}</Typography>
                  <Typography sx={{ color: INK, fontWeight: 600, fontSize: '1.15rem' }}>{typeof value === 'number' ? formatCount(value) : value}</Typography>
                </Box>
              ))}
            </Box>
            {phase === 'creating' && <LinearProgress sx={{ mb: 2 }} />}
            {!billable.length && (
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>Nothing to bill for {result.month.label}.</Alert>
            )}
            {result.drafts > 0 && (
              <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                {result.drafts} {result.drafts === 1 ? 'invoice is' : 'invoices are'} saved as drafts because something needs checking
                (shown below). Customers don't see drafts until you approve them.
              </Alert>
            )}

            <TableContainer sx={{ maxHeight: 440, border: `1px solid ${RULE}`, borderRadius: 1.5 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, bgcolor: 'background.paper' } }}>
                    <TableCell>Account</TableCell>
                    <TableCell>Charges</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    <TableCell align="right">GST</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billable.map((row) => (
                    <TableRow key={row.accountId} sx={{ '& td': { verticalAlign: 'top', fontVariantNumeric: 'tabular-nums' } }}>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" sx={{ color: INK, fontWeight: 500 }}>{row.account}</Typography>
                        <Typography variant="caption" sx={{ color: MUTED }}>{row.customer}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 240 }}>
                        {row.lines.map((l, i) => (
                          <Typography key={i} variant="caption" sx={{ display: 'block', color: INK }}>
                            {l.name}: {l.quantity > 1 ? `${formatCount(l.quantity)} × ₹${l.unitPrice} = ` : ''}{formatINR(l.amount)}
                          </Typography>
                        ))}
                        {row.problems.map((p, i) => (
                          <Typography key={`p${i}`} variant="caption" sx={{ display: 'block', color: '#C2410C' }}>{p}</Typography>
                        ))}
                      </TableCell>
                      <TableCell align="right">{formatINR(row.subtotal)}</TableCell>
                      <TableCell align="right">{formatINR(row.taxAmount)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatINR(row.total)}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: row.status === 'draft' ? '#C77700' : INK, display: 'block' }}>
                          {row.action === 'created' ? (phase === 'done' ? 'Created' : 'New') : (phase === 'done' ? 'Updated' : 'Update')}
                          {row.status === 'draft' ? ' as draft' : ''}
                        </Typography>
                        {row.invoiceNumber && <Typography variant="caption" sx={{ color: MUTED }}>{row.invoiceNumber}</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {skipped.length > 0 && (
              <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1.5 }}>
                Skipped {skipped.length}: {skipped.slice(0, 6).map((r) => `${r.account} (${r.reason})`).join('; ')}
                {skipped.length > 6 ? `; and ${skipped.length - 6} more` : ''}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {phase === 'done' ? (
          <Button variant="contained" onClick={onClose} sx={buttonSx}>Done</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy} sx={buttonSx}>Cancel</Button>
            {(phase === 'choose' || phase === 'previewing') && (
              <Button
                variant="contained"
                onClick={() => run(true)}
                disabled={busy}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ ...buttonSx, fontWeight: 600 }}
              >
                {busy ? 'Preparing preview…' : `Preview ${monthLabel}`}
              </Button>
            )}
            {(phase === 'preview' || phase === 'creating') && (
              <>
                <Button onClick={() => setPhase('choose')} disabled={busy} sx={buttonSx}>Change month</Button>
                <Button
                  variant="contained"
                  onClick={() => run(false)}
                  disabled={busy || !billable.length}
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ ...buttonSx, fontWeight: 600 }}
                >
                  {busy ? 'Creating invoices…' : `Create ${formatCount(billable.length)} ${billable.length === 1 ? 'invoice' : 'invoices'}`}
                </Button>
              </>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const RowMenu = ({ invoice, onView, onPay, onApprove, onCancel }) => {
  const [anchor, setAnchor] = useState(null);
  const close = () => setAnchor(null);
  const canPay = ['pending', 'failed', 'draft'].includes(invoice.status);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
      <Button size="small" onClick={() => onView(invoice)} sx={{ ...buttonSx, fontWeight: 600 }}>
        View
      </Button>
      <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label={`More actions for ${invoice.invoiceNumber}`} aria-haspopup="menu">
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem component="a" href={billingService.generatePdfUrl(invoice._id)} target="_blank" rel="noopener noreferrer" onClick={close}>
          <ListItemText primary="Download PDF" />
        </MenuItem>
        {invoice.status === 'draft' && (
          <MenuItem onClick={() => { close(); onApprove(invoice); }}><ListItemText primary="Approve and send to customer" /></MenuItem>
        )}
        {canPay && (
          <MenuItem onClick={() => { close(); onPay(invoice); }}><ListItemText primary="Mark as paid" /></MenuItem>
        )}
        {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
          <MenuItem onClick={() => { close(); onCancel(invoice); }} sx={{ color: '#C2410C' }}><ListItemText primary="Cancel invoice" /></MenuItem>
        )}
        {(invoice.status === 'paid' || invoice.status === 'cancelled') && (
          <MenuItem disabled><ListItemText primary={invoice.status === 'paid' ? `Paid ${formatDay(invoice.paidDate)}` : 'Cancelled'} /></MenuItem>
        )}
      </Menu>
    </Box>
  );
};

const InvoiceManagement = () => {
  const months = useMemo(() => monthOptions(), []);
  const [month, setMonth] = useState('');
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [generateOpen, setGenerateOpen] = useState(false);
  const [paying, setPaying] = useState(null);
  const [payment, setPayment] = useState({ paymentMethod: 'manual', transactionId: '', paidDate: todayInput() });
  const [confirm, setConfirm] = useState(null); // { invoice, status }
  const [viewing, setViewing] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulk, setBulk] = useState(null); // { status, ids }
  const [exportOpts, setExportOpts] = useState(null); // { month, status } while the export dialog is open
  const [exporting, setExporting] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await invoiceService.list({ month: month || undefined, limit: 2000 });
      setInvoices(response.data || []);
    } catch (err) {
      setLoadError(err.response?.data?.error || 'Invoices could not be loaded. Try again.');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices
      .filter((inv) => !status || (status === 'overdue' ? daysOverdue(inv) > 0 : inv.status === status))
      .filter((inv) => !kind || (inv.kind || 'subscription') === kind)
      .filter((inv) => !q || [inv.invoiceNumber, inv.user?.company, inv.user?.name, inv.user?.email,
        inv.whatsappAccount?.businessName, inv.whatsappAccount?.verifiedName, inv.whatsappAccount?.phoneNumber]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [invoices, status, kind, search]);

  // Only rows still on screen count as selected, so changing a filter never acts on hidden invoices
  const selectedRows = useMemo(() => filtered.filter((inv) => selected.has(inv._id)), [filtered, selected]);
  const allSelected = filtered.length > 0 && selectedRows.length === filtered.length;
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map((inv) => inv._id)));
  const bulkOptions = BULK_ACTIONS
    .map((action) => ({ ...action, ids: selectedRows.filter((inv) => action.from.includes(inv.status)).map((inv) => inv._id) }))
    .filter((action) => action.ids.length > 0);

  const figures = useMemo(() => {
    const sum = (list) => list.reduce((s, inv) => s + totalOf(inv), 0);
    const byStatus = (st) => invoices.filter((inv) => inv.status === st);
    return {
      billed: sum(invoices.filter((inv) => inv.status !== 'cancelled' && inv.status !== 'draft')),
      pending: byStatus('pending'),
      overdue: invoices.filter((inv) => daysOverdue(inv) > 0),
      paid: byStatus('paid'),
      drafts: byStatus('draft')
    };
  }, [invoices]);

  const replace = (updated) => {
    setInvoices((prev) => prev.map((inv) => (inv._id === updated._id ? { ...inv, ...updated } : inv)));
    setViewing((v) => (v && v._id === updated._id ? { ...v, ...updated } : v));
  };
  const openPay = (invoice) => { setPayment({ paymentMethod: 'manual', transactionId: '', paidDate: todayInput() }); setPaying(invoice); };

  const savePayment = async () => {
    try {
      setWorking(true);
      const response = await invoiceService.markPaid(paying._id, payment);
      replace(response.data);
      setSuccess(`${paying.invoiceNumber} marked as paid`);
      setPaying(null);
    } catch (err) {
      setError(err.response?.data?.error || 'The payment could not be recorded. Try again.');
    } finally {
      setWorking(false);
    }
  };

  const openBulk = (action) => {
    setPayment({ paymentMethod: 'manual', transactionId: '', paidDate: todayInput() });
    setBulk({ status: action.status, ids: action.ids });
  };

  const saveBulk = async () => {
    try {
      setWorking(true);
      const response = await invoiceService.bulkStatus(bulk.ids, bulk.status, bulk.status === 'paid' ? payment : {});
      const { updated, skipped } = response.data;
      const byId = new Map(updated.map((inv) => [inv._id, inv]));
      setInvoices((prev) => prev.map((inv) => (byId.has(inv._id) ? { ...inv, ...byId.get(inv._id) } : inv)));
      setSelected(new Set());
      const count = `${formatCount(updated.length)} ${updated.length === 1 ? 'invoice' : 'invoices'}`;
      if (skipped.length) {
        setError(`${count} ${BULK_COPY[bulk.status].done}. Skipped ${skipped.length}: ${skipped.slice(0, 5).map((x) => `${x.invoiceNumber} (${x.reason})`).join(', ')}${skipped.length > 5 ? '…' : ''}`);
      } else {
        setSuccess(`${count} ${BULK_COPY[bulk.status].done}`);
      }
      setBulk(null);
    } catch (err) {
      setError(err.response?.data?.error || 'The invoices could not be updated. Try again.');
    } finally {
      setWorking(false);
    }
  };

  // CSV for Tally; the dialog starts from the month and status on screen
  const openExport = () => setExportOpts({
    kind: kind === 'subscription' ? 'subscription' : 'monthly',
    month: month || months[1].key,
    status: ['pending', 'paid', 'failed'].includes(status) ? status : ''
  });

  const exportTally = async () => {
    try {
      setExporting(true);
      const params = { kind: exportOpts.kind, month: exportOpts.month || undefined, status: exportOpts.status || undefined };
      const blob = await invoiceService.exportTallyCsv(params);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tally-${params.kind === 'subscription' ? 'subscriptions' : 'messages'}-${params.month || 'all-months'}${params.status ? `-${params.status}` : ''}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setExportOpts(null);
    } catch (err) {
      let message = 'The export could not be created. Try again.';
      // Errors come back as a blob because of the download response type
      try { message = JSON.parse(await err.response.data.text()).error || message; } catch (e) { /* keep the default */ }
      setError(message);
    } finally {
      setExporting(false);
    }
  };

  const saveStatus = async () => {
    try {
      setWorking(true);
      const response = await invoiceService.setStatus(confirm.invoice._id, confirm.status);
      replace(response.data);
      setSuccess(confirm.status === 'pending' ? `${confirm.invoice.invoiceNumber} sent to the customer` : `${confirm.invoice.invoiceNumber} cancelled`);
      setConfirm(null);
    } catch (err) {
      setError(err.response?.data?.error || 'The invoice could not be updated. Try again.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <AppLayout>
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 3 }}>
          <Box>
            <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}>
              Invoices
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.75 }}>
              Monthly bills for plan fees and WhatsApp messages, with GST.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={openExport}
              sx={buttonSx}
            >
              Export for Tally
            </Button>
            <Button variant="contained" startIcon={<ReceiptLongOutlinedIcon />} onClick={() => setGenerateOpen(true)} sx={{ ...buttonSx, fontWeight: 600 }}>
              Generate monthly bills
            </Button>
          </Box>
        </Box>

        {!loading && invoices.length > 0 && (
          <FigureStrip
            figures={[
              { label: 'Billed', value: formatINR(figures.billed), hint: month ? months.find((m) => m.key === month)?.label : 'All months' },
              {
                label: 'Awaiting payment',
                value: formatCount(figures.pending.length),
                hint: `${formatINR(figures.pending.reduce((s, inv) => s + totalOf(inv), 0))}${figures.overdue.length ? `, ${formatCount(figures.overdue.length)} overdue` : ''}`
              },
              { label: 'Paid', value: formatCount(figures.paid.length), hint: formatINR(figures.paid.reduce((s, inv) => s + totalOf(inv), 0)) },
              { label: 'Drafts to check', value: formatCount(figures.drafts.length), hint: figures.drafts.length ? 'Approve or fix before sending' : 'None' }
            ]}
          />
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.5, md: 2 }, mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search invoice, customer or number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: MUTED }} /></InputAdornment> }}
            sx={{ flex: { xs: '1 1 100%', md: '1 1 280px' }, maxWidth: { md: 360 }, bgcolor: 'background.paper' }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="inv-month">Month</InputLabel>
            <Select labelId="inv-month" label="Month" value={month} onChange={(e) => setMonth(e.target.value)}>
              <MenuItem value="">All months</MenuItem>
              {months.map((m) => <MenuItem key={m.key} value={m.key}>{m.label.replace(' (so far)', '')}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="inv-kind">Type</InputLabel>
            <Select labelId="inv-kind" label="Type" value={kind} onChange={(e) => setKind(e.target.value)}>
              <MenuItem value="">All types</MenuItem>
              <MenuItem value="monthly">Message charges</MenuItem>
              <MenuItem value="subscription">Subscriptions</MenuItem>
            </Select>
          </FormControl>
          <ToggleButtonGroup size="small" exclusive value={status} onChange={(e, v) => v !== null && setStatus(v)} sx={{ flexWrap: 'wrap' }}>
            <ToggleButton value="" sx={{ ...buttonSx, px: 1.5 }}>All</ToggleButton>
            <ToggleButton value="pending" sx={{ ...buttonSx, px: 1.5 }}>Awaiting payment</ToggleButton>
            <ToggleButton value="paid" sx={{ ...buttonSx, px: 1.5 }}>Paid</ToggleButton>
            <ToggleButton value="overdue" sx={{ ...buttonSx, px: 1.5 }}>Overdue</ToggleButton>
            <ToggleButton value="failed" sx={{ ...buttonSx, px: 1.5 }}>Payment failed</ToggleButton>
            <ToggleButton value="draft" sx={{ ...buttonSx, px: 1.5 }}>Drafts</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {selectedRows.length > 0 && (
          <Box
            role="region"
            aria-label="Actions for selected invoices"
            sx={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5, px: 2, py: 1,
              border: `1px solid ${RULE}`, borderRadius: 2, bgcolor: 'rgba(37, 211, 102, 0.06)',
              position: 'sticky', top: 80, zIndex: 2
            }}
          >
            <Typography variant="body2" sx={{ color: INK, fontWeight: 600, mr: 1 }}>
              {formatCount(selectedRows.length)} selected
              <Box component="span" sx={{ color: MUTED, fontWeight: 400 }}>, {formatINR(selectedRows.reduce((sum, inv) => sum + totalOf(inv), 0))}</Box>
            </Typography>
            {bulkOptions.map((action) => (
              <Button
                key={action.status}
                size="small"
                variant={action.status === 'paid' ? 'contained' : 'outlined'}
                color={action.danger ? 'error' : 'primary'}
                onClick={() => openBulk(action)}
                sx={buttonSx}
              >
                {action.label}{action.ids.length < selectedRows.length ? ` (${formatCount(action.ids.length)})` : ''}
              </Button>
            ))}
            {!bulkOptions.length && (
              <Typography variant="body2" sx={{ color: MUTED }}>No status change applies to all of these.</Typography>
            )}
            <Button size="small" onClick={() => setSelected(new Set())} sx={{ ...buttonSx, ml: 'auto' }}>Clear selection</Button>
          </Box>
        )}

        {loadError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>{loadError}</Alert>}

        <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ p: 3 }}>{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={44} />)}</Box>
          ) : !filtered.length ? (
            <Box sx={{ p: { xs: 3, sm: 5 } }}>
              <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>{invoices.length ? 'No invoices match these filters' : 'No invoices yet'}</Typography>
              <Typography variant="body2" sx={{ color: MUTED, mb: 2.5 }}>
                {invoices.length ? 'Try another month or status.' : 'Generate the bills for a month to create invoices for every WhatsApp account.'}
              </Typography>
              {!invoices.length && <Button variant="outlined" onClick={() => setGenerateOpen(true)} sx={buttonSx}>Generate monthly bills</Button>}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ minWidth: 880 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, borderColor: RULE, py: 1.25 } }}>
                    <TableCell padding="checkbox" sx={{ pl: 1 }}>
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={selectedRows.length > 0 && !allSelected}
                        onChange={toggleAll}
                        inputProps={{ 'aria-label': allSelected ? 'Clear selection' : `Select all ${filtered.length} invoices shown` }}
                      />
                    </TableCell>
                    <TableCell sx={{ pl: 0 }}>Invoice</TableCell>
                    <TableCell>Customer and account</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right" sx={{ pr: 2 }}><Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Actions</Box></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((inv) => (
                    <TableRow
                      key={inv._id}
                      hover
                      selected={selected.has(inv._id)}
                      onClick={(e) => { if (!e.target.closest('button, a, input, [role="menu"]')) setViewing(inv); }}
                      sx={{ cursor: 'pointer', '& td': { borderColor: RULE, py: 1.25, fontVariantNumeric: 'tabular-nums' } }}
                    >
                      <TableCell padding="checkbox" sx={{ pl: 1 }}>
                        <Checkbox
                          size="small"
                          checked={selected.has(inv._id)}
                          onChange={() => toggleOne(inv._id)}
                          inputProps={{ 'aria-label': `Select ${inv.invoiceNumber}` }}
                        />
                      </TableCell>
                      <TableCell sx={{ pl: 0 }}>
                        <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }}>{inv.invoiceNumber}</Typography>
                        <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>{KIND_LABELS[inv.kind || 'subscription']}</Typography>
                        <Typography variant="caption" sx={{ color: MUTED }}>Issued {formatDay(inv.createdAt)}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="body2" sx={{ color: INK }} noWrap>{inv.user?.company || inv.user?.name || '–'}</Typography>
                        <Typography variant="caption" sx={{ color: MUTED, display: 'block' }} noWrap>
                          {inv.whatsappAccount ? `${inv.whatsappAccount.verifiedName || inv.whatsappAccount.businessName}, ${inv.whatsappAccount.phoneNumber}` : (inv.billing?.plan ? `${inv.billing.plan} plan` : '')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: INK, whiteSpace: 'nowrap' }}>
                          {inv.billingMonth
                            ? new Date(`${inv.billingMonth}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
                            : `${formatDay(inv.billingPeriodStart)} to ${formatDay(inv.billingPeriodEnd)}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: MUTED }}>Due {formatDay(inv.dueDate)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }}>{formatINR(totalOf(inv))}</Typography>
                        {inv.taxAmount > 0 && <Typography variant="caption" sx={{ color: MUTED }}>incl. GST {formatINR(inv.taxAmount)}</Typography>}
                      </TableCell>
                      <TableCell>
                        <StatusDot status={inv.status} overdue={daysOverdue(inv)} />
                        {inv.status === 'draft' && inv.notes && (
                          <Typography variant="caption" sx={{ color: '#C77700', display: 'block', maxWidth: 240 }} title={inv.notes} noWrap>{inv.notes}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 2 }}>
                        <RowMenu
                          invoice={inv}
                          onView={setViewing}
                          onPay={openPay}
                          onApprove={(invoice) => setConfirm({ invoice, status: 'pending' })}
                          onCancel={(invoice) => setConfirm({ invoice, status: 'cancelled' })}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <GenerateDialog
          open={generateOpen}
          month={months[1].key}
          onClose={() => setGenerateOpen(false)}
          onCreated={(result) => {
            setMonth(result.month.key);
            setSuccess(`${result.created + result.updated} invoices ready for ${result.month.label}`);
            load();
          }}
        />

        <InvoiceDetailDialog
          invoice={viewing}
          open={!!viewing}
          onClose={() => setViewing(null)}
          onPay={openPay}
          onApprove={(invoice) => setConfirm({ invoice, status: 'pending' })}
          onCancel={(invoice) => setConfirm({ invoice, status: 'cancelled' })}
        />

        {/* Mark as paid */}
        <Dialog open={!!paying} onClose={() => !working && setPaying(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Mark {paying?.invoiceNumber} as paid</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>
              {paying && `${paying.user?.company || paying.user?.name}, ${formatINR(totalOf(paying))}`}
            </Typography>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="pay-method">Payment method</InputLabel>
                <Select labelId="pay-method" label="Payment method" value={payment.paymentMethod} onChange={(e) => setPayment({ ...payment, paymentMethod: e.target.value })}>
                  {Object.entries(PAYMENT_METHODS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" label="Reference (UTR, cheque number)" value={payment.transactionId} onChange={(e) => setPayment({ ...payment, transactionId: e.target.value })} />
              <TextField size="small" type="date" label="Paid on" value={payment.paidDate} onChange={(e) => setPayment({ ...payment, paidDate: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setPaying(null)} disabled={working} sx={buttonSx}>Cancel</Button>
            <Button variant="contained" onClick={savePayment} disabled={working} startIcon={working ? <CircularProgress size={16} color="inherit" /> : null} sx={buttonSx}>
              {working ? 'Saving…' : 'Mark as paid'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Export for Tally */}
        <Dialog open={!!exportOpts} onClose={() => !exporting && setExportOpts(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Export for Tally</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: MUTED, mb: 2.5 }}>
              {exportOpts?.kind === 'subscription'
                ? 'A CSV with one row per subscription invoice: plan, period and fee before GST.'
                : 'A CSV with one row per message charges invoice: message counts, rates and values by category, and the total before GST.'}
              {' '}Drafts and cancelled invoices are left out.
            </Typography>
            {exportOpts && (
              <Box sx={{ display: 'grid', gap: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="export-kind">Type</InputLabel>
                  <Select labelId="export-kind" label="Type" value={exportOpts.kind} onChange={(e) => setExportOpts({ ...exportOpts, kind: e.target.value })}>
                    <MenuItem value="monthly">Message charges</MenuItem>
                    <MenuItem value="subscription">Subscriptions</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel id="export-month">Month</InputLabel>
                  <Select labelId="export-month" label="Month" value={exportOpts.month} onChange={(e) => setExportOpts({ ...exportOpts, month: e.target.value })}>
                    {months.map((m) => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
                    <MenuItem value="">All months</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel id="export-status">Invoices</InputLabel>
                  <Select labelId="export-status" label="Invoices" value={exportOpts.status} onChange={(e) => setExportOpts({ ...exportOpts, status: e.target.value })}>
                    <MenuItem value="">All sent invoices</MenuItem>
                    <MenuItem value="pending">Awaiting payment</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="failed">Payment failed</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setExportOpts(null)} disabled={exporting} sx={buttonSx}>Cancel</Button>
            <Button
              variant="contained"
              onClick={exportTally}
              disabled={exporting}
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlinedIcon />}
              sx={{ ...buttonSx, fontWeight: 600 }}
            >
              {exporting ? 'Exporting…' : 'Download CSV'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk status change */}
        <Dialog open={!!bulk} onClose={() => !working && setBulk(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ color: INK, fontWeight: 600 }}>
            {bulk && `${BULK_COPY[bulk.status].title}: ${formatCount(bulk.ids.length)} ${bulk.ids.length === 1 ? 'invoice' : 'invoices'}`}
          </DialogTitle>
          <DialogContent>
            {bulk && (
              <Typography variant="body2" sx={{ color: MUTED, mb: bulk.status === 'paid' ? 2 : 0 }}>
                {formatINR(invoices.filter((inv) => bulk.ids.includes(inv._id)).reduce((sum, inv) => sum + totalOf(inv), 0))} in total.{' '}
                {bulk.status === 'paid'
                  ? 'The same payment details are recorded on each invoice, and customers are notified.'
                  : BULK_COPY[bulk.status].body}
                {bulk.ids.length < selectedRows.length && ` The other ${formatCount(selectedRows.length - bulk.ids.length)} selected invoices don't change.`}
              </Typography>
            )}
            {bulk?.status === 'paid' && (
              <Box sx={{ display: 'grid', gap: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="bulk-pay-method">Payment method</InputLabel>
                  <Select labelId="bulk-pay-method" label="Payment method" value={payment.paymentMethod} onChange={(e) => setPayment({ ...payment, paymentMethod: e.target.value })}>
                    {Object.entries(PAYMENT_METHODS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField size="small" label="Reference (optional)" value={payment.transactionId} onChange={(e) => setPayment({ ...payment, transactionId: e.target.value })} />
                <TextField size="small" type="date" label="Paid on" value={payment.paidDate} onChange={(e) => setPayment({ ...payment, paidDate: e.target.value })} InputLabelProps={{ shrink: true }} />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setBulk(null)} disabled={working} sx={buttonSx}>Back</Button>
            <Button
              variant="contained"
              color={bulk?.status === 'cancelled' ? 'error' : 'primary'}
              onClick={saveBulk}
              disabled={working}
              startIcon={working ? <CircularProgress size={16} color="inherit" /> : null}
              sx={buttonSx}
            >
              {working ? 'Saving…' : bulk && BULK_COPY[bulk.status].button}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Approve or cancel */}
        <Dialog open={!!confirm} onClose={() => !working && setConfirm(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ color: INK, fontWeight: 600 }}>
            {confirm?.status === 'pending' ? `Send ${confirm?.invoice.invoiceNumber} to the customer?` : `Cancel ${confirm?.invoice.invoiceNumber}?`}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: MUTED }}>
              {confirm?.status === 'pending'
                ? 'The invoice becomes visible to the customer as awaiting payment, and they are notified.'
                : "The invoice stays on record as cancelled and isn't counted as billed."}
            </Typography>
            {confirm?.invoice.notes && (
              <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>{confirm.invoice.notes}</Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirm(null)} disabled={working} sx={buttonSx}>Back</Button>
            <Button
              variant="contained"
              color={confirm?.status === 'cancelled' ? 'error' : 'primary'}
              onClick={saveStatus}
              disabled={working}
              startIcon={working ? <CircularProgress size={16} color="inherit" /> : null}
              sx={buttonSx}
            >
              {confirm?.status === 'pending' ? 'Send invoice' : 'Cancel invoice'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ width: '100%' }}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={5000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setSuccess(null)} severity="success" variant="filled" sx={{ width: '100%' }}>{success}</Alert>
        </Snackbar>
      </Container>
    </AppLayout>
  );
};

export default InvoiceManagement;
